import api from './api';
import type { OutreachMessage } from '@radevents/shared';

/** Woher eine Anfrage stammt. */
export type InquiryKind = 'CONTACT' | 'PARTNER';

/** Bearbeitungsstand im Dashboard-Posteingang. */
export type InquiryStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE';

/** Höchstens so viele Dateien je Anfrage — muss zu AttachmentBytes.MAX_PER_INQUIRY passen. */
export const MAX_ATTACHMENTS = 3;

/** Größenlimit je Datei in Bytes — muss zu AttachmentBytes.MAX_BYTES passen. */
export const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024;

/** Was der Dateidialog anbietet; das Backend prüft zusätzlich die Magic Bytes. */
export const ACCEPTED_ATTACHMENTS = 'image/jpeg,image/png,image/webp,application/pdf';

/**
 * Ab diesem Wert gilt eine Anfrage als Spam und liegt nur noch in der Verdachtsansicht.
 * Muss zu InquirySpamCheck.SPAM_THRESHOLD passen.
 */
export const SPAM_THRESHOLD = 50;

export interface InquiryRequest {
  name: string;
  company?: string;
  email: string;
  topic?: string;
  message?: string;
  /** Honeypot — bleibt bei Menschen leer. */
  website?: string;
  /** Millisekunden zwischen Anzeigen und Absenden des Formulars. */
  elapsedMs?: number;
}

export interface InquiryAttachment {
  id: number;
  inquiryId: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

/** Eine gespeicherte Anfrage, wie sie Admins/Moderatoren sehen. */
export interface Inquiry {
  id: number;
  kind: InquiryKind;
  name: string;
  company?: string | null;
  email: string;
  topic?: string | null;
  message?: string | null;
  status: InquiryStatus;
  formDurationMs?: number | null;
  handledBy?: number | null;
  handledByName?: string | null;
  handledAt?: string | null;
  spamScore: number;
  spamReason?: string | null;
  attachments: InquiryAttachment[];
  createdAt: string;
}

/** Zähler für die Filterkarten über dem Posteingang. */
export interface InquiryCounts {
  all: number;
  open: number;
  inProgress: number;
  done: number;
  spam: number;
}

export interface InquiryPage {
  content: Inquiry[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/** Einreichung aus dem Formular der Kontaktseite. Liefert die Id für etwaige Anhänge. */
export async function submitContactInquiry(payload: InquiryRequest): Promise<number> {
  const response = await api.post('/inquiries/contact', payload);
  return response.data.id;
}

/** Einreichung aus dem Kontakt-Modal der „Für Partner"-Seite. */
export async function submitPartnerInquiry(payload: InquiryRequest): Promise<number> {
  const response = await api.post('/inquiries/partner', payload);
  return response.data.id;
}

/**
 * Hängt eine Datei an eine gerade eingereichte Anfrage. Zweistufig, weil die Datei die Id
 * braucht — das Backend lässt das nur kurz nach dem Einreichen zu.
 */
export async function uploadInquiryAttachment(inquiryId: number, file: File): Promise<void> {
  const form = new FormData();
  form.append('file', file);
  // Der Axios-Client schickt sonst sein globales application/json und damit keine
  // Multipart-Boundary — der Server kann den Rumpf dann nicht lesen.
  await api.post(`/inquiries/${inquiryId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/** Posteingang (ADMIN/MODERATOR). Neueste zuerst, paginiert. */
export async function getInquiries(
  params: { kind?: InquiryKind; status?: InquiryStatus; spam?: boolean; q?: string; page?: number; size?: number } = {}
): Promise<InquiryPage> {
  const { kind, status, spam, q, page = 0, size = 20 } = params;
  const response = await api.get('/inquiries', { params: { kind, status, spam, q: q || undefined, page, size } });
  return response.data;
}

/**
 * Zähler für die Filterkarten. Eigener Aufruf, weil die Liste paginiert ist — aus einer Seite
 * lässt sich nicht ablesen, wie viele Anfragen insgesamt offen sind.
 */
export async function getInquiryCounts(params: { kind?: InquiryKind; q?: string } = {}): Promise<InquiryCounts> {
  const response = await api.get('/inquiries/counts', { params: { kind: params.kind, q: params.q || undefined } });
  return response.data;
}

/** Manuell als Spam markieren — oder die Markierung zurücknehmen. */
export async function setInquirySpam(id: number, spam: boolean): Promise<Inquiry> {
  const response = await api.post(`/inquiries/${id}/spam`, null, { params: { spam } });
  return response.data;
}

/** Anzahl unerledigter Anfragen ohne Spamverdacht — für die Badge in der Navigation. */
export async function getUnhandledInquiryCount(): Promise<{ count: number }> {
  const response = await api.get('/inquiries/unhandled-count');
  return response.data;
}

export async function updateInquiryStatus(id: number, status: InquiryStatus): Promise<Inquiry> {
  const response = await api.patch(`/inquiries/${id}/status`, { status });
  return response.data;
}

/**
 * Lädt einen Anhang herunter. Der Endpunkt verlangt den Bearer-Token, ein einfacher Link
 * würde ihn nicht mitschicken — also über einen Blob und eine Object-URL.
 */
export async function downloadInquiryAttachment(a: InquiryAttachment): Promise<void> {
  const res = await api.get(`/inquiries/${a.inquiryId}/attachments/${a.id}`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = a.filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Antworten aus dem Dashboard (V164) ────────────────────────────────────────────────

/**
 * Eine Nachricht im Verlauf einer Anfrage. Dieselbe Form wie im Outreach-Postfach, damit die
 * Blase von dort (`Bubble`) sie ohne Umbau zeigen kann; Anhänge gibt es hier nicht.
 */
export type InquiryMessage = Omit<OutreachMessage, 'contactId' | 'templateId' | 'dismissedAt'> & {
  inquiryId: number;
};

/** Worüber geantwortet wird. `enabled = false`: kein CONTACT_FROM konfiguriert. */
export interface InquiryMailbox {
  enabled: boolean;
  address?: string | null;
  inboxActive: boolean;
  lastPolledAt?: string | null;
  lastError?: string | null;
}

export async function getInquiryMailbox(): Promise<InquiryMailbox> {
  const response = await api.get('/inquiries/mailbox');
  return response.data;
}

export async function getInquiryMessages(id: number): Promise<InquiryMessage[]> {
  const response = await api.get(`/inquiries/${id}/messages`);
  return (response.data as Omit<InquiryMessage, 'attachments'>[]).map((m) => ({ ...m, attachments: [] }));
}

/** Stellt eine Antwort in die Warteschlange. Ohne Betreff setzt der Server einen passenden. */
export async function sendInquiryReply(id: number, payload: { subject?: string; body: string }): Promise<InquiryMessage> {
  const response = await api.post(`/inquiries/${id}/messages`, payload);
  return { ...response.data, attachments: [] };
}

const inquiryService = {
  getInquiryCounts,
  setInquirySpam,
  submitContactInquiry,
  submitPartnerInquiry,
  uploadInquiryAttachment,
  getInquiries,
  getUnhandledInquiryCount,
  updateInquiryStatus,
  downloadInquiryAttachment,
  getInquiryMailbox,
  getInquiryMessages,
  sendInquiryReply,
};
export default inquiryService;
